import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Bell, Heart, ShieldCheck, TrendDown, Receipt } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Vertical "Today on PriceGuard" live feed for the hero right column.
 *
 * Pulls from the public anonymised endpoint `/api/public/live-activity`
 * so the feed reflects real, recent activity across the user base. If
 * the backend is unreachable or the user base is brand-new (no events
 * yet) we fall back to a small set of illustrative examples so the hero
 * still looks alive. Either way the panel never shows zero state.
 *
 * Privacy: only retailer + category + amount + timestamp are shown.
 * No user names, no product names, no order numbers.
 */

const FALLBACK_EVENTS = [
  {
    minsAgo: 8,
    icon: TrendDown,
    accent: "#16A34A",
    accentBg: "rgba(22, 163, 74, 0.08)",
    label: "Drop detected",
    product: "JB Hi-Fi · TV",
    detail: "Within retailer price adjustment window",
    value: "$249 to recover",
  },
  {
    minsAgo: 22,
    icon: Heart,
    accent: "#16A34A",
    accentBg: "rgba(22, 163, 74, 0.08)",
    label: "Wishlist drop",
    product: "Harvey Norman · Appliance",
    detail: "Watching daily",
    value: "$180 cheaper",
  },
  {
    minsAgo: 41,
    icon: Bell,
    accent: "#002FA7",
    accentBg: "rgba(0, 47, 167, 0.06)",
    label: "Claim email drafted",
    product: "PB Tech · Laptop",
    detail: "Polite, retailer-ready",
    value: "Ready to send",
  },
  {
    minsAgo: 73,
    icon: ShieldCheck,
    accent: "#D97706",
    accentBg: "rgba(217, 119, 6, 0.06)",
    label: "Warranty reminder",
    product: "Manufacturer cover ending",
    detail: "30 days to go",
    value: "Reminder sent",
  },
];

function timeAgoMins(iso) {
  if (!iso) return 999;
  const then = new Date(iso).getTime();
  if (!then) return 999;
  return Math.max(0, Math.floor((Date.now() - then) / 60000));
}

function relativeLabel(minsAgo) {
  if (minsAgo < 1) return "Just now";
  if (minsAgo === 1) return "1 min ago";
  if (minsAgo < 60) return `${minsAgo} mins ago`;
  const h = Math.floor(minsAgo / 60);
  if (h < 24) return h === 1 ? "1 hr ago" : `${h} hrs ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? "1 day ago" : `${d} days ago`;
}

/**
 * Shape a raw drop event from /api/public/live-activity into the card
 * format the feed renders. The endpoint returns
 *   { kind, retailer_name, category, amount, created_at }
 * (no product name on purpose).
 */
function buildEventFromDrop(d) {
  const cat = (d.category || "product").toLowerCase();
  return {
    minsAgo: timeAgoMins(d.created_at),
    icon: TrendDown,
    accent: "#16A34A",
    accentBg: "rgba(22, 163, 74, 0.08)",
    label: "Drop detected",
    product: `${d.retailer_name} · ${cat}`,
    detail: "Within the retailer's price adjustment window",
    value: `$${Math.round(d.amount).toLocaleString()} to recover`,
  };
}

/**
 * Add a couple of aggregate "what else happened" cards so the feed
 * covers all three pillars (drops + wishlist + receipts/warranty) and
 * doesn't accidentally feel like just a price-drop ticker. These are
 * derived from aggregate counts only - never tied to a specific user
 * event - so they're privacy-safe by design.
 */
function buildAggregateEvents(data) {
  const counts = data?.counts || {};
  const out = [];
  if ((counts.wishlist_being_watched || 0) > 0) {
    out.push({
      minsAgo: 18,
      icon: Heart,
      accent: "#16A34A",
      accentBg: "rgba(22, 163, 74, 0.08)",
      label: "Wishlist items watched",
      product: `${counts.wishlist_being_watched.toLocaleString()} items · across NZ retailers`,
      detail: "Hoping for the next big drop",
      value: "Watching daily",
    });
  }
  if ((counts.receipts_this_week || 0) > 0) {
    out.push({
      minsAgo: 36,
      icon: Receipt,
      accent: "#D97706",
      accentBg: "rgba(217, 119, 6, 0.06)",
      label: "Receipts filed this week",
      product: `${counts.receipts_this_week.toLocaleString()} receipts vaulted`,
      detail: "Warranty alerts armed and ready",
      value: "Safe + searchable",
    });
  }
  return out;
}

export default function HeroLiveFeed() {
  const [data, setData] = useState(null);
  const [visibleCount, setVisibleCount] = useState(0);

  // Build the final events array: prefer real anonymised drops, top them
  // up with aggregate-count cards, and fall back to illustrative examples
  // if there's nothing real yet. Memoised so we don't reshuffle on every
  // staggered render tick.
  const events = useMemo(() => {
    if (!data) return FALLBACK_EVENTS;
    const drops = Array.isArray(data.drops) ? data.drops.slice(0, 3).map(buildEventFromDrop) : [];
    const aggregates = buildAggregateEvents(data);
    const real = [...drops, ...aggregates];
    if (real.length === 0) return FALLBACK_EVENTS;
    return real.slice(0, 4);
  }, [data]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/public/live-activity`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { /* fall back silently */ });
    return () => { cancelled = true; };
  }, []);

  // Staggered fade-in once the events array is settled.
  useEffect(() => {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      setVisibleCount(events.length);
      return;
    }
    setVisibleCount(0);
    let i = 0;
    const interval = setInterval(() => {
      i += 1;
      setVisibleCount(i);
      if (i >= events.length) clearInterval(interval);
    }, 380);
    return () => clearInterval(interval);
  }, [events]);

  return (
    <div className="bg-[#0a0a0a] text-white p-6 lg:p-7 relative overflow-hidden" data-testid="hero-live-feed">
      {/* subtle radial gradient for depth */}
      <div
        aria-hidden
        className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, #002FA7 0%, transparent 70%)" }}
      />

      <div className="relative flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-[#16A34A] pg-pulse" />
          <span className="pg-overline text-white/70">Today on PriceGuard</span>
        </div>
        <span className="text-[10px] font-mono-pg tracking-widest uppercase text-white/40">Live · anonymised</span>
      </div>

      <div className="relative space-y-3" data-testid="hero-live-feed-items">
        {events.map((evt, i) => {
          const Icon = evt.icon;
          const visible = i < visibleCount;
          return (
            <div
              key={`${evt.label}-${i}`}
              className={`bg-white text-[#0a0a0a] p-4 transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}
              aria-hidden={!visible}
              data-testid={`hero-live-feed-item-${i}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-8 h-8 flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: evt.accentBg }}
                >
                  <Icon size={16} weight="fill" color={evt.accent} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="pg-overline" style={{ color: evt.accent }}>{evt.label}</div>
                    <div className="text-[10px] font-mono-pg text-[#52525b] uppercase tracking-widest flex-shrink-0">
                      {relativeLabel(evt.minsAgo)}
                    </div>
                  </div>
                  <div className="font-bold text-sm mt-1 truncate">{evt.product}</div>
                  <div className="text-xs text-[#52525b] mt-0.5">{evt.detail}</div>
                  <div className="text-sm font-bold mt-1" style={{ color: evt.accent }}>{evt.value}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="relative mt-5 text-[10px] font-mono-pg text-white/40 uppercase tracking-widest">
        Privacy-safe by design · retailer + category only · no names, no products
      </div>
    </div>
  );
}
